// #336 slice 5, brief §5.1: the rule of a slot, read back from its cells.
// Candidates are fitted parameter by parameter, each is generated and diffed
// with ruleExceptions, and the one with the fewest exceptions wins (the
// simpler on a tie). So what the editor calls «a mano» is by construction
// what the rule does not write. Too many exceptions: «valori a mano».
// Keep free of React and any UI import.
import type { SlotDraft } from "./programEdit.js";
import {
  DEFAULT_LOAD_STEP,
  generateRounds,
  harderSign,
  isEffortAxis,
  progressIndex,
  readRound,
  ruleExceptions,
  seqAt,
  liftSeq,
  type GenerateOptions,
  type ProgressionRule,
  type ReadSet,
  type RuleException,
  type Sequence,
} from "./programProgression.js";
import type { AmrapStop, RoundType } from "./schemas/programV3.js";

/** The names of §5.4b, in the order they are tried (ruleName). */
export type RuleName =
  | "repsThenLoad" | "triple" | "waves" | "blocks" | "pyramid" | "topBackoff" | "effortRamp" | "tetrad"
  | "perSet" | "timed" | "history" | "repsUp" | "linear" | "constant";

export type Recognition =
  | { kind: "rule"; name: RuleName; rule: ProgressionRule; exceptions: RuleException[] }
  | { kind: "manual" };

/** The most frequent value; the first seen on a tie. */
const mode = (xs: readonly number[]): number | undefined => {
  const n = new Map<number, number>();
  let best: number | undefined;
  for (const x of xs) {
    const k = Math.round(x * 1000) / 1000;
    n.set(k, (n.get(k) ?? 0) + 1);
    if (best === undefined || n.get(k)! > n.get(best)!) best = k;
  }
  return best;
};

/** #355: the stop most test AMRAPs carry, «none» counted; first seen on a tie. */
const modeStop = (xs: ReadonlyArray<AmrapStop | undefined>): AmrapStop | undefined => {
  const keyOf = (x: AmrapStop | undefined) => (!x ? "" : x.kind === "rir" ? `rir${x.rir}` : x.kind);
  const counts = xs.map((x) => xs.filter((y) => keyOf(y) === keyOf(x)).length);
  return xs[counts.indexOf(Math.max(0, ...counts))];
};

const stepped = (s: Sequence | null): boolean =>
  s !== null && ((s.kind === "linear" && s.step !== 0) || (s.kind === "ramp" && s.from !== s.to) || s.kind === "cycle");

const nonZero = (xs: readonly number[] | undefined): boolean => (xs ?? []).some((x) => x !== 0);

/** 0, d, 2d, … with d not 0. */
const byStep = (xs: readonly number[] | undefined): boolean =>
  !!xs && xs.length >= 2 && xs[1] !== 0 && xs.every((x, i) => Math.abs(x - i * xs[1]!) < 1e-6);

/** 0, then the same easier d on every other set. */
const backoff = (xs: readonly number[] | undefined, axis: ProgressionRule["axis"]): boolean =>
  !!xs && xs.length >= 2 && xs[0] === 0 && xs[1]! * harderSign(axis) < 0 && xs.every((x, i) => i === 0 || x === xs[1]);

/** The rule's name, by §5.4b: reps and load stepping set by set is the
 *  pyramid; one top set and easier ones after it, top and back-off; a step
 *  on the effort is a ramp; effects in test and deload with no step are the
 *  tetrad; then per set, and the rest. Reps that step with no intensity are
 *  rising reps. */
const ruleName = (r: ProgressionRule): RuleName => {
  if (r.threshold) return r.threshold.setsMax !== undefined ? "triple" : "repsThenLoad";
  if (r.value?.kind === "cycle") return "waves";
  if (r.value?.kind === "blocks" || r.reps.kind === "blocks") return "blocks";
  if (!isEffortAxis(r.axis) && byStep(r.perSet?.reps) && byStep(r.perSet?.value)) return "pyramid";
  if (isEffortAxis(r.axis) && !nonZero(r.perSet?.reps) && backoff(r.perSet?.value, r.axis)) return "topBackoff";
  if (isEffortAxis(r.axis) && stepped(r.value)) return "effortRamp";
  const anyStep = stepped(r.value) || stepped(r.reps);
  const effects =
    r.test?.amrapLast === true || !!r.test?.delta || !!r.deload?.delta ||
    (r.deload?.factor ?? 1) !== 1 || !!r.deload?.setsDelta;
  if (effects && !anyStep) return "tetrad";
  if (nonZero(r.perSet?.value) || nonZero(r.perSet?.reps)) return "perSet";
  if (r.work === "countdown") return "timed";
  if (r.axis === "history") return "history";
  if (r.axis === "none" && stepped(r.reps)) return "repsUp";
  return anyStep ? "linear" : "constant";
};

/** How much a sequence costs to explain, for the tie-break. */
const complexity = (s: Sequence | null): number =>
  s === null ? 0
    : s.kind === "constant" ? 0
      : s.kind === "linear" ? (s.cap === undefined ? 2 : 3)
        : s.kind === "ramp" ? 4 : s.kind === "blocks" ? 5 : 6;

type Point = { w: number; b: number };

/** Sequences that could explain `pts`, the round values at rounds `w`. */
const seqCandidates = (pts: readonly Point[], types: readonly RoundType[]): Sequence[] => {
  const bs = pts.map((p) => p.b);
  const ps = pts.map((p) => progressIndex(types, p.w));
  const out: Sequence[] = [{ kind: "constant", value: mode(bs) ?? 0 }];
  for (let j = 1; j < pts.length; j += 1) {
    const dp = ps[j]! - ps[j - 1]!;
    const step = dp > 0 ? (bs[j]! - bs[j - 1]!) / dp : 0;
    if (step === 0) continue;
    const start = Math.round((bs[j - 1]! - ps[j - 1]! * step) * 1000) / 1000;
    out.push({ kind: "linear", start, step });
    out.push({ kind: "linear", start, step, cap: step > 0 ? Math.max(...bs) : Math.min(...bs) });
  }
  const last = pts.length - 1;
  if (last >= 2 && bs[0] !== bs[last]) out.push({ kind: "ramp", from: bs[0]!, to: bs[last]!, rounds: ps[last]! + 1 });
  const seen = new Set<string>();
  return out.filter((s) => (seen.has(JSON.stringify(s)) ? false : (seen.add(JSON.stringify(s)), true)));
};

/** Cycles that repeat at least once over `pts` (every round the slot runs
 *  in, deloads included: a cycle is indexed by the round). */
const cycleCandidates = (pts: readonly Point[]): Sequence[] => {
  const out: Sequence[] = [];
  for (let L = 2; L + 2 <= pts.length; L += 1) {
    const values = Array.from({ length: L }, (_, k) => mode(pts.filter((p) => p.w % L === k).map((p) => p.b)) ?? pts[0]!.b);
    if (new Set(values).size > 1) out.push({ kind: "cycle", values });
  }
  return out;
};

const misses = (seq: Sequence, pts: readonly Point[], types: readonly RoundType[], from = 0): number =>
  pts.filter((p) => Math.abs(seqAt(seq, p.w - from, types.slice(from)) - p.b) > 1e-6).length;

/** The same shape restarted after each deload round, block k lifted by its
 *  own amount (§5.3 «a blocchi»). Null with fewer than two blocks. */
const blocksCandidate = (pts: readonly Point[], types: readonly RoundType[], n: number): Sequence | null => {
  const ends: number[] = [];
  for (let w = 0; w < n - 1; w += 1) if (types[w] === "deload") ends.push(w + 1);
  const starts = [0, ...ends];
  const segs = starts.map((s, k) => ({ s, e: starts[k + 1] ?? n }));
  const ptsOf = (seg: { s: number; e: number }) => pts.filter((p) => p.w >= seg.s && p.w < seg.e);
  if (segs.length < 2 || segs.some((seg) => ptsOf(seg).length === 0)) return null;
  const first = ptsOf(segs[0]!);
  const local = types.slice(0, segs[0]!.e);
  const seq1 = seqCandidates(first, local)
    .map((seq) => ({ seq, cost: misses(seq, first, local) * 100 + complexity(seq) }))
    .sort((a, b) => a.cost - b.cost)[0]!.seq;
  const blocks = segs.map((seg) => {
    const lift = mode(ptsOf(seg).map((p) => p.b - seqAt(seq1, p.w - seg.s, types.slice(seg.s)))) ?? 0;
    return { rounds: seg.e - seg.s, seq: liftSeq(seq1, lift) };
  });
  return { kind: "blocks", blocks };
};

type Field = "reps" | "value";
type Fit = { rule: ProgressionRule; cost: number };

/** The rule of a slot whose rounds are `types`, or «valori a mano». */
export const recognizeRule = (
  slot: SlotDraft,
  types: readonly RoundType[],
  opts: GenerateOptions = {},
): Recognition => {
  const row0 = slot.setRows[0];
  if (!row0 || row0.workKind === "countup") return { kind: "manual" };
  const timed = row0.workKind === "countdown";
  const n = slot.inactiveWeeks.length;
  const reads = new Map<number, ReadSet[]>();
  for (let w = 0; w < n; w += 1) {
    const r = readRound(slot, w, timed);
    if (r && r.length > 0) reads.set(w, r);
  }
  const typeAt = (w: number) => types[w] ?? "normal";
  const active = [...reads.keys()];
  const prog = active.filter((w) => typeAt(w) !== "deload");
  if (prog.length === 0) return { kind: "manual" };
  const sets = mode(prog.map((w) => reads.get(w)!.length)) ?? 1;
  const deloads = active.filter((w) => typeAt(w) === "deload");
  const tests = active.filter((w) => typeAt(w) === "test");
  const amrapTests = tests.filter((w) => reads.get(w)!.at(-1)!.amrap);
  const stop = modeStop(amrapTests.map((w) => slot.setRows[reads.get(w)!.length - 1]?.cells[w]?.amrapStop));
  const allSets = active.flatMap((w) => reads.get(w)!);
  const rangeKey = mode(allSets.filter((s) => !s.amrap).map((s) => (s.range ? s.range.min * 1000 + s.range.max : -1)));
  const setsDelta = mode(deloads.map((w) => reads.get(w)!.length - sets));

  const skeleton: ProgressionRule = {
    axis: row0.intensityKind,
    ...(timed ? { work: "countdown" as const } : {}),
    sets,
    reps: { kind: "constant", value: 0 },
    value: null,
    ...(rangeKey !== undefined && rangeKey >= 0 ? { range: { min: Math.floor(rangeKey / 1000), max: rangeKey % 1000 } } : {}),
    ...(tests.length > 0 ? { test: amrapTests.length * 2 > tests.length ? { amrapLast: true, ...(stop && { amrapStop: stop }) } : {} } : {}),
    ...(deloads.length > 0 ? { deload: setsDelta ? { setsDelta } : {} } : {}),
  };
  const cost = (rule: ProgressionRule, field?: Field): number =>
    ruleExceptions(slot, rule, types, opts).filter((e) => !field || e.field === field).length;

  /** Offsets per set to try: none, and each progressing round's own shape. */
  const offsetsOf = (field: Field): number[][] => {
    const out: number[][] = [Array(sets).fill(0)];
    for (const w of prog) {
      const r = reads.get(w)!;
      const v0 = r[0]![field];
      if (r.length !== sets || v0 === null || r.some((s) => s[field] === null)) continue;
      const o = r.map((s) => Math.round((s[field]! - v0) * 1000) / 1000);
      if (!out.some((x) => x.join() === o.join())) out.push(o);
    }
    return out;
  };
  const pointsOf = (field: Field, o: readonly number[], rounds: readonly number[]): Point[] =>
    rounds.flatMap((w) => {
      const b = mode(reads.get(w)!.flatMap((s, i) => (s[field] === null ? [] : [s[field]! - (o[Math.min(i, o.length - 1)] ?? 0)])));
      return b === undefined ? [] : [{ w, b }];
    });

  const fitField = (field: Field, base: ProgressionRule): Fit => {
    let best: Fit = { rule: base, cost: Infinity };
    for (const o of offsetsOf(field)) {
      const perSet = nonZero(o) ? { ...base.perSet, [field]: o } : withoutField(base.perSet, field);
      const normal = prog.filter((w) => typeAt(w) === "normal");
      const pools = [pointsOf(field, o, prog), pointsOf(field, o, normal)].filter((p) => p.length > 0);
      const seqs = pools.flatMap((pts) => {
        const b = blocksCandidate(pts, types, n);
        return [...seqCandidates(pts, types), ...(b ? [b] : [])];
      });
      seqs.push(...cycleCandidates(pointsOf(field, o, active)));
      for (const seq of seqs) {
        const rule: ProgressionRule = { ...base, [field]: seq, ...(perSet ? { perSet } : {}) };
        if (!perSet) delete rule.perSet;
        for (const r of field === "value" ? effectVariants(rule) : [rule]) {
          const c = cost(r, field) * 100 + complexity(seq) + (nonZero(o) ? 1 : 0) + (r.test?.delta ? 1 : 0);
          if (c < best.cost) best = { rule: r, cost: c };
        }
      }
    }
    return best;
  };

  /** The rule with each test and deload effect the residuals suggest. */
  const effectVariants = (rule: ProgressionRule): ProgressionRule[] => {
    const gen = generateRounds(rule, types, n, opts);
    /** Have minus generated, or have over generated to `grain`; on the last
     *  set, every set, or the hardest generated ones. */
    const resid = (rounds: number[], lastOnly: boolean, grain = 0, topOnly = false): number[] =>
      rounds.flatMap((w) => {
        const have = reads.get(w)!;
        const want = gen[w]!;
        const vs = want.flatMap((s) => (s.value === null ? [] : [s.value]));
        const top = harderSign(rule.axis) > 0 ? Math.max(...vs) : Math.min(...vs);
        const idx = lastOnly
          ? [Math.min(have.length, want.length) - 1]
          : have.map((_, i) => i).filter((i) => !topOnly || want[i]?.value === top);
        return idx.flatMap((i) => {
          const h = have[i]?.value;
          const g = want[i]?.value;
          if (h == null || g == null || i >= want.length || (grain > 0 && g === 0)) return [];
          return [grain > 0 ? Math.round((h / g) / grain) * grain : h - g];
        });
      });
    const testVariants: Array<ProgressionRule["test"]> = [rule.test];
    if (rule.test) {
      const all = mode(resid(tests, false));
      const last = mode(resid(tests, true));
      const top = mode(resid(tests, false, 0, true));
      if (all) testVariants.push({ ...rule.test, delta: all, on: "all" });
      if (last) testVariants.push({ ...rule.test, delta: last, on: "last" });
      if (top) testVariants.push({ ...rule.test, delta: top, on: "top" });
    }
    const dels: Array<ProgressionRule["deload"]> = [rule.deload];
    if (rule.deload) {
      // On a load row a factor comes first, so it wins a tie (§5.3). Its load
      // was rounded: try it to 5 % first, then to 1 %.
      for (const grain of isEffortAxis(rule.axis) ? [] : [0.05, 0.01]) {
        const factor = mode(resid(deloads, false, grain));
        if (factor !== undefined && factor !== 1) dels.push({ ...rule.deload, factor: Math.round(factor * 100) / 100 });
      }
      const delta = mode(resid(deloads, false));
      if (delta) dels.push({ ...rule.deload, delta });
    }
    return testVariants.flatMap((test) => dels.map((deload) => ({ ...rule, ...(test ? { test } : {}), ...(deload ? { deload } : {}) })));
  };

  const withReps = fitField("reps", skeleton).rule;
  const valueless = row0.intensityKind === "none" || row0.intensityKind === "history";
  let best = valueless ? withReps : fitField("value", { ...withReps, value: { kind: "constant", value: 0 } }).rule;
  // The threshold starts from the skeleton's effects, not the ones fitted
  // for a rule without it; effectVariants tries the rest, none first.
  let thr = thresholdCandidate({ ...best, test: skeleton.test, deload: skeleton.deload && {} }, reads, prog);
  if (thr?.deload) {
    // A deload's set count read against the threshold's own count there.
    const gen = generateRounds(thr, types, n, opts);
    const d = mode(deloads.map((w) => reads.get(w)!.length - gen[w]!.length));
    if (d) thr = { ...thr, deload: { setsDelta: d } };
  }
  // A load that never rose in the program shows no step: the unit's own, when
  // it too leaves the rounds as they are, so a reopened rule keeps a real one.
  if (thr?.threshold && thr.threshold.loadStep === 0) {
    const stepped = { ...thr, threshold: { ...thr.threshold, loadStep: opts.loadQuantum ?? DEFAULT_LOAD_STEP } };
    if (cost(stepped) <= cost(thr)) thr = stepped;
  }
  if (thr) {
    const variants = effectVariants(thr);
    const top = variants.map((r) => ({ r, c: cost(r) })).sort((a, b) => a.c - b.c)[0]!;
    const evidence = top.r.threshold!.loadStep > 0 || top.r.range !== undefined;
    if (top.c < cost(best) || (top.c === cost(best) && evidence)) best = top.r;
  }
  const exceptions = ruleExceptions(slot, best, types, opts);
  if (exceptions.length > manualLimit(allSets.length)) return { kind: "manual" };
  return { kind: "rule", name: ruleName(best), rule: best, exceptions };
};

/** More exceptions than this and the row reads «valori a mano»: a quarter
 *  of its sets, and never fewer than two. */
const manualLimit = (activeSets: number): number => Math.max(2, Math.ceil(activeSets / 4));

const withoutField = (p: ProgressionRule["perSet"], field: Field): ProgressionRule["perSet"] => {
  if (!p) return undefined;
  const rest = { ...p };
  delete rest[field];
  return rest.reps || rest.value ? rest : undefined;
};

/** «Ripetizioni poi carico» or the triple progression, read from a load row
 *  whose reps move. Null when there is nothing to read it from. */
const thresholdCandidate = (
  rule: ProgressionRule,
  reads: Map<number, ReadSet[]>,
  prog: readonly number[],
): ProgressionRule | null => {
  if (rule.work || (rule.axis !== "absolute" && rule.axis !== "percent-of-max")) return null;
  const reps = prog.map((w) => mode(reads.get(w)!.flatMap((s) => (s.reps === null ? [] : [s.reps]))) ?? 0);
  const load = prog.map((w) => mode(reads.get(w)!.flatMap((s) => (s.value === null ? [] : [s.value]))) ?? 0);
  const counts = prog.map((w) => reads.get(w)!.length);
  if (new Set(reps).size < 2) return null;
  const up = (xs: number[]) => mode(xs.slice(1).map((x, j) => x - xs[j]!).filter((d) => d > 0));
  const repsMin = rule.range?.min ?? Math.min(...reps);
  const repsMax = rule.range?.max ?? Math.max(...reps);
  const setsMax = Math.max(...counts);
  const threshold = {
    repsMin,
    repsMax,
    repsStep: up(reps) ?? 1,
    ...(setsMax > counts[0]! ? { setsMax } : {}),
    loadStart: load[0]!,
    loadStep: up(load) ?? 0,
  };
  const out: ProgressionRule = { ...rule, sets: counts[0]!, reps: { kind: "constant", value: repsMin }, value: null, threshold };
  delete out.perSet;
  return out;
};
